import { ReactNode, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Stack from "@mui/material/Stack";
import { useIsMobile } from "@/hooks/useIsMobile";

type SortDirection = "asc" | "desc";

export interface Column<T> {
  /** Unique key for the column */
  key: string;
  /** Header label */
  header: string;
  /** Cell renderer */
  render: (item: T) => ReactNode;
  /** Header cell alignment */
  align?: "left" | "center" | "right";
  /** Minimum width for desktop table */
  minWidth?: number;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Extract a sortable value from the item. Required if sortable is true. */
  sortValue?: (item: T) => string | number | boolean | null | undefined;
}

export interface ResponsiveTableProps<T> {
  /** Data array to render */
  data: T[];
  /** Column definitions for desktop table view */
  columns: Column<T>[];
  /** Render function for mobile card view */
  mobileCardRender: (item: T, index: number) => ReactNode;
  /** Function to get unique key for each item */
  getKey: (item: T) => string | number;
  /** Optional empty state to render when no data */
  emptyState?: ReactNode;
  /** Optional loading state */
  loading?: boolean;
  /** Optional loading skeleton (renders N times) */
  loadingSkeleton?: ReactNode;
  /** Number of skeleton items to show when loading */
  skeletonCount?: number;
  /** Optional header content (title, actions) */
  header?: ReactNode;
  /** Optional footer content (load more, pagination) */
  footer?: ReactNode;
  /** Minimum table width for horizontal scroll on tablet */
  minTableWidth?: number;
  /** Default sort column key */
  defaultSortKey?: string;
  /** Default sort direction */
  defaultSortDirection?: SortDirection;
  /** Callback when a row is clicked */
  onRowClick?: (item: T) => void;
}

/**
 * ResponsiveTable component that renders as:
 * - Cards on mobile (< 900px)
 * - Traditional table on desktop (>= 900px)
 *
 * @example
 * <ResponsiveTable
 *   data={contacts}
 *   columns={[
 *     { key: 'name', header: 'Name', render: (c) => c.name },
 *     { key: 'email', header: 'Email', render: (c) => c.email },
 *   ]}
 *   mobileCardRender={(contact) => <ContactCard contact={contact} />}
 *   getKey={(c) => c.id}
 * />
 */
function compareSortValues(
  a: string | number | boolean | null | undefined,
  b: string | number | boolean | null | undefined,
  direction: SortDirection,
): number {
  // Nulls/undefined always sort last
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  let result: number;
  if (typeof a === "string" && typeof b === "string") {
    result = a.localeCompare(b, undefined, { sensitivity: "base" });
  } else if (typeof a === "boolean" && typeof b === "boolean") {
    result = a === b ? 0 : a ? -1 : 1;
  } else {
    result = Number(a) - Number(b);
  }

  return direction === "asc" ? result : -result;
}

export function ResponsiveTable<T>({
  data,
  columns,
  mobileCardRender,
  getKey,
  emptyState,
  loading,
  loadingSkeleton,
  skeletonCount = 5,
  header,
  footer,
  minTableWidth = 650,
  defaultSortKey,
  defaultSortDirection = "asc",
  onRowClick,
}: ResponsiveTableProps<T>) {
  const isMobile = useIsMobile();
  const [sortKey, setSortKey] = useState<string | undefined>(defaultSortKey);
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(defaultSortDirection);

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortValue) return data;
    const accessor = col.sortValue;
    return [...data].sort((a, b) =>
      compareSortValues(accessor(a), accessor(b), sortDirection),
    );
  }, [data, sortKey, sortDirection, columns]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  // Loading state
  if (loading && data.length === 0 && loadingSkeleton) {
    return (
      <Box>
        {header}
        <Stack spacing={2} sx={{ p: { xs: 0, md: 0 } }}>
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <Box key={i}>{loadingSkeleton}</Box>
          ))}
        </Stack>
        {footer}
      </Box>
    );
  }

  // Empty state
  if (!loading && data.length === 0 && emptyState) {
    return (
      <Box>
        {header}
        {emptyState}
        {footer}
      </Box>
    );
  }

  // Mobile: Card view
  if (isMobile) {
    return (
      <Box>
        {header}
        <Stack spacing={2}>
          {sortedData.map((item, index) => (
            <Box key={getKey(item)}>{mobileCardRender(item, index)}</Box>
          ))}
        </Stack>
        {footer}
      </Box>
    );
  }

  // Desktop: Table view
  return (
    <Box>
      {header}
      <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <Table sx={{ minWidth: minTableWidth }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{ minWidth: col.minWidth }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortKey === col.key}
                      direction={sortKey === col.key ? sortDirection : "asc"}
                      onClick={() => handleSort(col.key)}
                    >
                      {col.header}
                    </TableSortLabel>
                  ) : (
                    col.header
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((item) => (
              <TableRow
                key={getKey(item)}
                hover
                onClick={onRowClick ? () => onRowClick(item) : undefined}
                sx={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {columns.map((col) => (
                  <TableCell key={col.key} align={col.align || "left"}>
                    {col.render(item)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {footer}
    </Box>
  );
}
